import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Zap, LogOut } from "lucide-react";

const NAV = {
  admin: [
    { to: "/dashboard", label: "Overview" },
    { to: "/riders", label: "Riders" },
    { to: "/onboarding", label: "Onboarding" },
    { to: "/vehicles", label: "Fleet" },
    { to: "/batteries", label: "Batteries" },
    { to: "/invoices", label: "Invoices" },
    { to: "/swap-history", label: "Swaps" },
    { to: "/employees", label: "Employees" },
  ],
  ops: [
    { to: "/onboarding", label: "Onboarding" },
    { to: "/riders", label: "Riders" },
  ],
  incharge: [{ to: "/swap", label: "Battery Swap" }],
};

const ROLE_LABEL = { admin: "Admin", incharge: "Swap Incharge", ops: "Ops Staff" };

function initials(name = "") {
  return name.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "U";
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = NAV[user?.role] || [];

  const pillCls = ({ isActive }) =>
    `rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors duration-150 whitespace-nowrap ${
      isActive ? "bg-slate-100 text-slate-900" : "text-slate-500 hover:text-slate-800"}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E4F3F2] via-[#F2F8FA] to-[#E6EFFB]" data-testid="app-layout">
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-4">
          <div className="flex items-center gap-2.5 shrink-0">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-teal-500 to-sky-500 flex items-center justify-center shadow-sm">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-bold text-[15px]">YuGO <span className="text-teal-600">Rides</span></div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Ops Command</div>
            </div>
          </div>

          <nav className="flex-1 flex justify-start md:justify-center gap-0.5 overflow-x-auto px-2">
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} className={pillCls}
                data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {it.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <span className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500" data-testid="live-indicator">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> Live
            </span>
            <div className="h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold"
              data-testid="user-avatar" title={`${user?.name} · ${ROLE_LABEL[user?.role]}`}>
              {initials(user?.name)}
            </div>
            <button onClick={async () => { await logout(); navigate("/login"); }}
              data-testid="logout-btn" title="Logout"
              className="text-slate-400 hover:text-slate-700 transition-colors">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">{children}</main>
    </div>
  );
}
